import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import styles from './CookieConsent.module.css'

const STORAGE_KEY = 'cookie-preferences'

interface CookiePreferences {
  necessary: true
  analytics: boolean
  marketing: boolean
  updatedAt: string
}

const getStoredPreferences = (): CookiePreferences | null => {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as CookiePreferences
  } catch (error) {
    console.warn('Failed to parse cookie preferences', error)
    return null
  }
}

const CookieConsent = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [analytics, setAnalytics] = useState(false)
  const [marketing, setMarketing] = useState(false)

  useEffect(() => {
    const prefs = getStoredPreferences()
    if (prefs) {
      setAnalytics(prefs.analytics)
      setMarketing(prefs.marketing)
      setIsOpen(false)
    } else {
      setIsOpen(true)
    }

    const handler = () => {
      const stored = getStoredPreferences()
      if (stored) {
        setAnalytics(stored.analytics)
        setMarketing(stored.marketing)
      } else {
        setAnalytics(false)
        setMarketing(false)
      }
      setIsOpen(true)
    }

    window.addEventListener('cookie-consent:open', handler)
    return () => {
      window.removeEventListener('cookie-consent:open', handler)
    }
  }, [])

  const persist = (prefs: CookiePreferences) => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs))
  }

  const acceptAll = () => {
    const prefs: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      updatedAt: new Date().toISOString(),
    }
    persist(prefs)
    setIsOpen(false)
  }

  const acceptSelection = () => {
    const prefs: CookiePreferences = {
      necessary: true,
      analytics,
      marketing,
      updatedAt: new Date().toISOString(),
    }
    persist(prefs)
    setIsOpen(false)
  }

  if (!isOpen) return null

  return (
    <div className={styles.banner}>
      <div className={styles.content}>
        <h3>Control your data</h3>
        <p>
          We use essential cookies to authenticate your session. Enable optional analytics and communications cookies to help us improve
          Transcripter.
        </p>
        <div className={styles.preferences}>
          <label className={styles.preferenceRow}>
            <Checkbox checked disabled />
            <div>
              <strong>Essential cookies</strong>
              <p>Required for secure login and workspace functionality.</p>
            </div>
          </label>
          <label className={styles.preferenceRow}>
            <Checkbox
              checked={analytics}
              onCheckedChange={(checked) => setAnalytics(Boolean(checked))}
            />
            <div>
              <strong>Analytics</strong>
              <p>Anonymous usage metrics to prioritise product improvements.</p>
            </div>
          </label>
          <label className={styles.preferenceRow}>
            <Checkbox
              checked={marketing}
              onCheckedChange={(checked) => setMarketing(Boolean(checked))}
            />
            <div>
              <strong>Marketing</strong>
              <p>Personalised onboarding tips and event invitations.</p>
            </div>
          </label>
        </div>
      </div>
      <div className={styles.actions}>
        <Button variant="ghost" onClick={() => setIsOpen(false)}>
          Cancel
        </Button>
        <Button variant="outline" onClick={acceptSelection}>
          Save selection
        </Button>
        <Button onClick={acceptAll}>Accept all</Button>
      </div>
    </div>
  )
}

export default CookieConsent
